import React, {useState} from 'react';
import {useNavigate, useParams} from "react-router-dom";
import {useQuery} from "react-query";
import ngage from "services/ngage";
import {Zone} from "types/Zone";
import {useNotifications} from "context/NotificationContext";
import Button from "components/Button";
import {PencilIcon, TrashIcon} from "@heroicons/react/solid";
import Page from "components/Page";
import Card from "components/Card";
import DescriptionList from "components/DescriptionList";
import DescriptionListItem from "components/DescriptionListItem";
import DescriptionTerm from "components/DescriptionTerm";
import DescriptionDetails from "components/DescriptionDetails";
import Chip from "components/Chip";
import DeleteZone from "pages/Zone/Delete";
import Link from 'components/Link';
import Empty from 'components/Empty';
import {LibraryIcon, OfficeBuildingIcon} from "@heroicons/react/outline";
import BooleanIndicator from "components/BooleanIndicator";
import {useOperator} from "context/OperatorContext";
import StackedList from "components/StackedList";
import StackedListItem from "components/StackedListItem";
import {toTitleCase} from "services/string";
import IconBadge from "components/IconBadge";
import IconName from "types/IconName";
import {useAuth} from "../../../context/AuthContext";
import NotAuthorised from "../../NotAuthorised";

// Component ----

function ViewZone() {
  const {termFor} = useOperator();
  const auth = useAuth();
  const {notify} = useNotifications();
  const navigate = useNavigate();
  const {id} = useParams();

  const [deletingZone, setDeletingZone] = useState<Zone | null>(null);

  const {data: zone, isLoading} = useQuery(
    ['zones', {id}],
    () => ngage.zones.get(Number(id)),
    {enabled: auth.can('zones:read')}
  );

  // Prepare action handlers
  function deleteZone() {
    if (zone) setDeletingZone(zone)
  }

  function editZone() {
    if (id) navigate(`/zones/${id}/edit`)
  }

  // Prevent unauthorised viewing
  if (auth.cannot('zones:read')) {
    return <NotAuthorised/>
  }

  // Prepare page actions and breadcrumbs
  const actions = (
    <>
      {auth.can('zones:write') &&
        <Button color={Button.colors.WHITE} icon={TrashIcon} onClick={deleteZone} disabled={!zone?.is_empty}>Delete</Button>
      }

      {auth.can('zones:write') &&
        <Button color={Button.colors.PRIMARY} icon={PencilIcon} onClick={editZone}>Edit</Button>
      }
    </>
  )

  const breadcrumbs = {
    "Locations": "/",
    [termFor('Zones')]: "/zones",
    [zone?.name || `View ${termFor('Zone')}`]: `/zones/${id}`
  }

  return (
    <Page
      title={zone?.name || `View ${termFor('Zone')}`}
      breadcrumbs={breadcrumbs}
      actions={actions}
      isLoading={isLoading}
    >
      {zone ?
        <>
          {/* Zone Information */}
          <section>
            <Card
              heading={`${termFor('Zone')} Information`}
              subheading={`${zone.exhibits.length} ${termFor('exhibits')} / ${zone.facilities.length} ${termFor('facilities')}`}
            >
              <DescriptionList>
                <DescriptionListItem span={1}>
                  <DescriptionTerm>{termFor('Site')}</DescriptionTerm>
                  <DescriptionDetails>
                    <Link to={`/sites/${zone.site.id}`}>{zone.site.name}</Link>
                  </DescriptionDetails>
                </DescriptionListItem>

                <DescriptionListItem span={1}>
                  <DescriptionTerm>Site Level</DescriptionTerm>
                  <DescriptionDetails>
                    {zone.level?.name}
                  </DescriptionDetails>
                </DescriptionListItem>

                <DescriptionListItem span={1}>
                  <DescriptionTerm>Restricted Area</DescriptionTerm>
                  <DescriptionDetails>
                    <BooleanIndicator value={zone.is_restricted}/>
                    {zone.restriction_reason &&
                      <span>{' '}{zone.restriction_reason}</span>
                    }
                  </DescriptionDetails>
                </DescriptionListItem>

                <DescriptionListItem span={1}>
                  <DescriptionTerm>Shown on Map</DescriptionTerm>
                  <DescriptionDetails>
                    <BooleanIndicator value={zone.is_mapped}/>
                  </DescriptionDetails>
                </DescriptionListItem>

                <DescriptionListItem span={2}>
                  <DescriptionTerm>Description</DescriptionTerm>
                  <DescriptionDetails>
                    <p className="whitespace-pre-line">
                      {zone.description ?? `A ${termFor('zone')} with ${zone.exhibit_count} ${termFor('exhibits')} and ${zone.facility_count} ${termFor('facilities')}`}
                    </p>
                  </DescriptionDetails>
                </DescriptionListItem>

                <DescriptionListItem span={1}>
                  <DescriptionTerm>Accessibility</DescriptionTerm>
                  <DescriptionDetails>
                    {!!zone.accessibility_criteria.length && Object.values(zone.accessibility_criteria).map(criterion => (
                      <IconBadge icon={IconName.HAND} text={toTitleCase(criterion)} color={IconBadge.colors.BRAND}/>
                    ))}

                    {!zone.accessibility_criteria.length &&
                      <p>No criteria listed</p>
                    }
                  </DescriptionDetails>
                </DescriptionListItem>

                <DescriptionListItem span={1}>
                  <DescriptionTerm>Sustainable</DescriptionTerm>
                  <DescriptionDetails>
                    <BooleanIndicator value={zone.is_sustainable}/>
                  </DescriptionDetails>
                </DescriptionListItem>

                <DescriptionListItem span={1}>
                  <DescriptionTerm>Standard Hours</DescriptionTerm>
                  <DescriptionDetails>
                    {!!zone.hours &&
                      <StackedList>
                        {Object
                          .entries(zone.hours)
                          .filter(([day]) => !['overflow', 'exceptions'].includes(day))
                          .map(([day, ranges], index) => (
                              <StackedListItem key={index}>
                                <div className="flex justify-between w-full">
                                  <div className="font-semibold">{toTitleCase(day)}</div>
                                  <div>
                                    {Array.isArray(ranges) ? (ranges.length > 0 ? ranges.join(', ') : 'Closed') : 'Closed'}
                                  </div>
                                </div>
                              </StackedListItem>
                            )
                          )
                        }
                      </StackedList>
                    }

                    {!zone.hours &&
                      <p>Open when {termFor('site')} is open</p>
                    }
                  </DescriptionDetails>
                </DescriptionListItem>
              </DescriptionList>
            </Card>
          </section>

          {/* Exhibits */}
          <section className="mt-4">
            <Card heading={termFor('Exhibits')}>
              {zone.exhibits.length ? (
                <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {zone.exhibits.map((exhibit, index) => (
                    <li key={index}>
                      <Chip
                        name={exhibit.name}
                        description={exhibit.type.name}
                        href={`/exhibits/${exhibit.id}`}
                        backgroundSrc={exhibit.media?.[0]?.preview_url}
                      />
                    </li>
                  ))}
                </ul>
              ) : (
                <Empty
                  icon={LibraryIcon}
                  text={`No ${termFor('Exhibits')}`}
                  hint={`Allocate ${termFor('exhibits')} to this ${termFor('zone')} to see them here.`}
                />
              )}
            </Card>
          </section>

          {/* Facilities */}
          <section className="mt-4">
            <Card heading={termFor('Facilities')}>
              {zone.facilities.length ? (
                <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {zone.facilities.map((facility, index) => (
                    <li key={index}>
                      <Chip
                        name={facility.name}
                        description={facility.description}
                        href={`/facilities/${facility.id}`}
                      />
                    </li>
                  ))}
                </ul>
              ) : (
                <Empty
                  icon={OfficeBuildingIcon}
                  text={`No ${termFor('Facilities')}`}
                  hint={`Allocate ${termFor('facilities')} to this ${termFor('zone')} to see them here.`}
                />
              )}
            </Card>
          </section>
        </>
        : null}

      {/* Delete confirmation modal */}
      <DeleteZone
        show={zone === deletingZone}
        zone={deletingZone}
        onCancel={() => setDeletingZone(null)}
        onDelete={zone => {
          navigate('/zones')
          notify("success", `${termFor('Zone')} Deleted`, `${zone.name} has been permanently removed`)
        }}
        onDeleteFail={zone => {
          setDeletingZone(null)
          notify("error", `${termFor('Zone')} Not Deleted`, `${zone.name} could not be removed`)
        }}
      />
    </Page>
  );
}

// Export ----

export default ViewZone;